import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/jghs7yj9_IMG-20240809-WA0006.jpg",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/er0az49x_IMG-20240809-WA0005.jpg",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/7yk0rcdy_9716e09a-869f-4b8a-a3ba-dad1bed1fc39.jfif",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/6rn211s0_f8097867-5919-4560-96a8-06374620ecb8.jfif",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/3cu4nomp_PHOTO-2025-01-24-16-38-38.jpg",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/snfddqxc_7e4e9d9f-7186-4e8a-8783-b7cb747cf253.jfif",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/m3n7jvcn_Web.jpg",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/rmmes6oo_339c7fff-568b-4413-af5d-d1fea53cd502.jfif",
  "https://customer-assets.emergentagent.com/job_vela-autentica/artifacts/aeuv7o6f_ab020bb7-76fc-4d4a-9fa3-62e06870689c.jfif",
];

const captions = [
  "Zorba de popa navegando a vela",
  "Zorba en la ría",
  "Dog friendly · a bordo del Zorba",
  "Zorba de costado con el Puente de Rande detrás",
  "Zorba navegando a Cíes",
  "A bordo del Zorba · cubierta de teca y velas al viento",
  "Zorba frente a Es Vedrá · Ibiza",
  "Puesta de sol desde el camarote de popa",
  "Zorba fondeado en la isla de Ibiza",
];

function Lightbox({ index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-10"
      style={{ backgroundColor: "rgba(10, 25, 47, 0.94)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
      >
        <X size={28} strokeWidth={1.6} />
      </button>

      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Anterior"
        className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 md:p-3 rounded-full hover:bg-white/10 transition-colors"
      >
        <ChevronLeft size={32} strokeWidth={1.5} />
      </button>

      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Siguiente"
        className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 md:p-3 rounded-full hover:bg-white/10 transition-colors"
      >
        <ChevronRight size={32} strokeWidth={1.5} />
      </button>

      <figure
        className="relative max-w-[92vw] max-h-[85vh] animate-[fadeIn_.25s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[index]}
          alt={captions[index]}
          className="max-w-full max-h-[85vh] object-contain rounded-sm shadow-2xl"
        />
        <figcaption className="mt-3 text-center text-sm text-white/70 font-sans">
          {captions[index]} · <span className="text-white/40">{index + 1} / {images.length}</span>
        </figcaption>
      </figure>
    </div>
  );
}

export default function Gallery() {
  const [index, setIndex] = useState(-1);

  const open = useCallback((i) => setIndex(i), []);
  const close = useCallback(() => setIndex(-1), []);
  const prev = useCallback(() => setIndex((i) => (i - 1 + images.length) % images.length), []);
  const next = useCallback(() => setIndex((i) => (i + 1) % images.length), []);

  return (
    <section id="galeria" className="section-pad bg-cream" data-testid="gallery-section">
      <div className="container-tight">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="label-eyebrow text-wood mb-4">Galería</p>
            <h2 className="font-heading text-4xl md:text-5xl leading-tight text-navy" data-testid="gallery-title">
              El Zorba en imágenes.
            </h2>
          </div>
          <p className="text-navy/70 text-base md:text-lg max-w-md leading-relaxed">
            Madera, mar, luz y navegación tranquila. Una mirada visual a la vida a bordo del Zorba y a la belleza de navegar por las Rías Baixas en un velero clásico.
            <span className="block mt-1 text-navy/50 text-sm">Haz clic en cualquier foto para verla en grande.</span>
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <button
            type="button"
            onClick={() => open(0)}
            data-testid="gallery-item-0"
            className="col-span-2 row-span-2 relative group overflow-hidden rounded-md cursor-zoom-in"
          >
            <img src={images[0]} alt={captions[0]} className="w-full h-full object-cover aspect-[4/5] md:aspect-auto md:h-full transition-transform duration-500 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors pointer-events-none" />
          </button>

          {[1, 2, 3, 4].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => open(i)}
              data-testid={`gallery-item-${i}`}
              className="relative group overflow-hidden rounded-md cursor-zoom-in aspect-square"
            >
              <img src={images[i]} alt={captions[i]} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors pointer-events-none" />
            </button>
          ))}

          <button
            type="button"
            onClick={() => open(5)}
            data-testid="gallery-item-5"
            className="col-span-2 md:col-span-2 relative group overflow-hidden rounded-md cursor-zoom-in"
          >
            <img src={images[5]} alt={captions[5]} className="w-full h-[260px] md:h-[300px] object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors pointer-events-none" />
          </button>

          <button
            type="button"
            onClick={() => open(6)}
            data-testid="gallery-item-6"
            className="col-span-2 md:col-span-2 relative group overflow-hidden rounded-md cursor-zoom-in"
          >
            <img src={images[6]} alt={captions[6]} className="w-full h-[260px] md:h-[300px] object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
            <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors pointer-events-none" />
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:gap-4">
          {[7, 8].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => open(i)}
              data-testid={`gallery-item-${i}`}
              className="relative group overflow-hidden rounded-md cursor-zoom-in aspect-[4/3]"
            >
              <img src={images[i]} alt={captions[i]} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors pointer-events-none" />
            </button>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a
            href="https://wa.me/34649927028?text=Hola%2C%20me%20gustar%C3%ADa%20ver%20disponibilidad%20del%20velero%20Zorba."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="gallery-availability-btn"
            className="btn-pill bg-navy text-white px-8 py-3.5 inline-block font-medium hover:bg-[#142949]"
          >
            Ver disponibilidad
          </a>
        </div>
      </div>

      {index >= 0 && (
        <Lightbox index={index} onClose={close} onPrev={prev} onNext={next} />
      )}
    </section>
  );
}
